import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { ChevronLeft, UserMinus, Save, X, Pencil, FileText, ArrowLeftRight } from "lucide-react";
import PhotoAvatar from "@/components/PhotoAvatar";
import ShareHistory from "@/components/ShareHistory";
import DevirDialog from "@/components/DevirDialog";

const label = "block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5";
const inputCls = "w-full px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white";

function InfoLine({ label: l, value }) {
  return (
    <div>
      <div className={label}>{l}</div>
      <div className="text-sm text-slate-900 font-mono-nums">{value || "—"}</div>
    </div>
  );
}

function DepartDialog({ open, onClose, onSubmit }) {
  const [f, setF] = useState({ ayrilis_tarihi: new Date().toISOString().slice(0,10), devir_edilen_kisi: "", yonetim_kurulu_karar_no: "", ayrilis_notu: "" });
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="crisp-card p-6 w-full max-w-lg" data-testid="depart-dialog">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">Ortaklıktan Çıkar</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={18}/></button>
        </div>
        <div className="space-y-4">
          <label>
            <span className={label}>Ayrılış Tarihi *</span>
            <input data-testid="depart-date" type="date" className={inputCls} value={f.ayrilis_tarihi} onChange={e=>setF({...f, ayrilis_tarihi: e.target.value})} />
          </label>
          <label>
            <span className={label}>Hisse Devredilen Kişi *</span>
            <input data-testid="depart-devir" className={inputCls} value={f.devir_edilen_kisi} onChange={e=>setF({...f, devir_edilen_kisi: e.target.value})} />
          </label>
          <label>
            <span className={label}>Yönetim Kurulu Karar No *</span>
            <input data-testid="depart-karar" className={inputCls} value={f.yonetim_kurulu_karar_no} onChange={e=>setF({...f, yonetim_kurulu_karar_no: e.target.value})} />
          </label>
          <label>
            <span className={label}>Not</span>
            <textarea data-testid="depart-not" rows={3} className={inputCls} value={f.ayrilis_notu} onChange={e=>setF({...f, ayrilis_notu: e.target.value})} />
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">İptal</button>
          <button data-testid="depart-confirm"
            onClick={() => {
              if (!f.devir_edilen_kisi.trim() || !f.yonetim_kurulu_karar_no.trim() || !f.ayrilis_tarihi) { toast.error("Zorunlu alanları doldurun"); return; }
              onSubmit(f);
            }}
            className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700">
            Ayrılışı Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MemberDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [openDepart, setOpenDepart] = useState(false);
  const [openDevir, setOpenDevir] = useState(false);

  const load = () => axios.get(`${API}/members/${id}`).then(r => { setM(r.data); setForm(r.data); });  useEffect(() => { load(); }, [id]);
  if (!m) return <div className="text-slate-500 text-sm">Yükleniyor...</div>;

  const set = (k,v) => setForm(s => ({ ...s, [k]: v }));

  const save = async () => {
    try {
      const payload = { ...form };
      ["misas_hisse","mis_holding_hisse","koop_hisse"].forEach(k => payload[k] = Number(payload[k]) || 0);
      await axios.patch(`${API}/members/${id}`, payload);
      toast.success("Güncellendi"); setEdit(false); load();
    } catch { toast.error("Güncelleme başarısız"); }
  };

  const depart = async (data) => {
    try {
      await axios.post(`${API}/members/${id}/depart`, data);
      toast.success("Ortak, ayrılanlar listesine taşındı");
      setOpenDepart(false);
      nav("/ayrilan");
    } catch { toast.error("İşlem başarısız"); }
  };

  return (
    <div className="space-y-6">
      <button data-testid="back-btn" onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ChevronLeft size={16}/> Geri
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-5 flex-wrap">
          <PhotoAvatar
            photoUrl={m.photo_url}
            name={m.ad_soyad}
            uploadUrl={`${API}/members/${id}/photo`}
            deleteUrl={`${API}/members/${id}/photo`}
            onChange={(url) => setM(prev => ({ ...prev, photo_url: url }))}
            size={96}
            editable={m.status !== "departed"}
          />
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
              {m.status === "departed" ? "Ayrılan Ortak" : "Aktif Ortak"}
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{m.ad_soyad}</h1>
            <div className="mt-2 flex flex-wrap gap-2">
              {m.misas_hisse>0 && <span className="inline-flex company-misas rounded-full px-3 py-1 text-xs font-bold">Misaş · {m.misas_hisse}</span>}
              {m.mis_holding_hisse>0 && <span className="inline-flex company-mh rounded-full px-3 py-1 text-xs font-bold">Mis Holding · {m.mis_holding_hisse}</span>}
              {m.koop_hisse>0 && <span className="inline-flex company-koop rounded-full px-3 py-1 text-xs font-bold">Koop · {m.koop_hisse}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!edit && (
            <button data-testid="edit-btn" onClick={() => setEdit(true)} className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Pencil size={14}/> Düzenle
            </button>
          )}
          {edit && (
            <>
              <button onClick={() => { setEdit(false); setForm(m); }} className="rounded-md border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">İptal</button>
              <button data-testid="save-btn" onClick={save} className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white px-3.5 py-2 text-sm font-semibold hover:bg-slate-800">
                <Save size={14}/> Kaydet
              </button>
            </>
          )}
          {m.status !== "departed" && !edit && (
            <button data-testid="devir-btn" onClick={() => setOpenDevir(true)} className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <ArrowLeftRight size={14}/> Devir Sözleşmesi
            </button>
          )}
          {m.status !== "departed" && !edit && (
            <button data-testid="depart-btn" onClick={() => setOpenDepart(true)} className="inline-flex items-center gap-2 rounded-md bg-red-600 text-white px-3.5 py-2 text-sm font-semibold hover:bg-red-700">
              <UserMinus size={14}/> Ortaklıktan Çıkar
            </button>
          )}
        </div>
      </div>

      {m.status === "departed" && (
        <div className="crisp-card p-6 bg-red-50/40 border-red-100">
          <h3 className="text-sm font-bold text-red-700 mb-4">Ayrılış Bilgileri</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <InfoLine label="Ayrılış Tarihi" value={m.ayrilis_tarihi} />
            <InfoLine label="Hisse Devredilen" value={m.devir_edilen_kisi} />
            <InfoLine label="YK Karar No" value={m.yonetim_kurulu_karar_no} />
          </div>
          {m.ayrilis_notu && <div className="mt-4 text-sm text-slate-700 whitespace-pre-wrap">{m.ayrilis_notu}</div>}
        </div>
      )}

      <div className="crisp-card p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-4">Kişisel Bilgiler</h3>
        {!edit ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <InfoLine label="Ad Soyad" value={m.ad_soyad}/>
            <InfoLine label="T.C. Kimlik" value={m.tc_kimlik}/>
            <InfoLine label="Üyelik Tarihi" value={m.uyelik_tarihi}/>
            <InfoLine label="Adres" value={m.adres}/>
            <InfoLine label="Tel 1" value={m.tel1}/>
            <InfoLine label="Tel 2" value={m.tel2}/>
            <InfoLine label="GSM 1" value={m.gsm1}/>
            <InfoLine label="GSM 2" value={m.gsm2}/>
            <InfoLine label="E-posta" value={m.email}/>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {["ad_soyad","tc_kimlik","uyelik_tarihi","adres","tel1","tel2","gsm1","gsm2","email"].map(k => (
              <label key={k}>
                <span className={label}>{k}</span>
                <input data-testid={`edit-${k}`} className={inputCls} value={form[k] || ""} onChange={e => set(k, e.target.value)}/>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          { key: "misas", label: "Misaş", cls: "company-misas" },
          { key: "mis_holding", label: "Mis Holding", cls: "company-mh" },
          { key: "koop", label: "Lok. Tük. Koop.", cls: "company-koop" },
        ].map(c => (
          <div key={c.key} className="crisp-card p-6">
            <div className="flex items-center justify-between gap-2">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${c.cls}`}>{c.label}</span>
              {!edit && (m[`${c.key}_hisse`] || 0) > 0 && (
                <a
                  data-testid={`cert-${c.key}`}
                  href={`${API}/members/${id}/certificate/${c.key}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 hover:text-slate-900 border border-slate-200 rounded-md px-2 py-1 bg-white hover:bg-slate-50"
                  title="Hisse Pay Belgesi (PDF)"
                >
                  <FileText size={12}/> PDF
                </a>
              )}
            </div>
            {!edit ? (
              <div className="mt-4 space-y-3">
                <InfoLine label="Hisse" value={m[`${c.key}_hisse`] || 0}/>
                <InfoLine label="Kayıt No" value={m[`${c.key}_kayit_no`]}/>
                <InfoLine label="Sayfa No" value={m[`${c.key}_sayfa_no`]}/>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <label><span className={label}>Hisse</span>
                  <input data-testid={`edit-${c.key}-hisse`} type="number" min="0" className={inputCls} value={form[`${c.key}_hisse`] || 0} onChange={e=>set(`${c.key}_hisse`, e.target.value)}/>
                </label>
                <label><span className={label}>Kayıt No</span>
                  <input className={inputCls} value={form[`${c.key}_kayit_no`] || ""} onChange={e=>set(`${c.key}_kayit_no`, e.target.value)}/>
                </label>
                <label><span className={label}>Sayfa No</span>
                  <input className={inputCls} value={form[`${c.key}_sayfa_no`] || ""} onChange={e=>set(`${c.key}_sayfa_no`, e.target.value)}/>
                </label>
              </div>
            )}
          </div>
        ))}
      </div>

      {m.notlar && !edit && (
        <div className="crisp-card p-6">
          <h3 className="text-sm font-bold text-slate-900 mb-3">Notlar</h3>
          <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{m.notlar}</div>
        </div>
      )}
      {edit && (
        <div className="crisp-card p-6">
          <span className={label}>Notlar</span>
          <textarea rows={4} className={inputCls} value={form.notlar || ""} onChange={e=>set("notlar", e.target.value)}/>
        </div>
      )}

      {!edit && <ShareHistory memberId={id} />}

      <DepartDialog open={openDepart} onClose={() => setOpenDepart(false)} onSubmit={depart} />
      <DevirDialog open={openDevir} onClose={() => setOpenDevir(false)} transferor={m} />
    </div>
  );
}
